import { useTranslation } from "react-i18next"
import { Upload } from "lucide-react"

import {
  SUPPORTED_ATTACHMENT_MIME_TYPES,
  buildFileAcceptString,
} from "@f2e/shared/constants"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
} from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"

import { useDragAndDrop } from "@/hooks"
import { useToast } from "@/hooks"
import { filterSupportedFiles } from "@/lib/fileUtils"

// Pre-built accept string for file input (includes .md extension workaround)
const FILE_ACCEPT_STRING = buildFileAcceptString(
  SUPPORTED_ATTACHMENT_MIME_TYPES
)

interface FileUploadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onFilesSelected: (files: File[]) => Promise<void>
}

export const FileUploadDialog = ({
  open,
  onOpenChange,
  onFilesSelected,
}: FileUploadDialogProps) => {
  const { t } = useTranslation()
  const { alertToast } = useToast()

  // Helper function to process files and handle unsupported types
  const processFiles = async (files: File[]) => {
    // Filter files into supported and unsupported
    const { supportedFiles, unsupportedFiles } = filterSupportedFiles(
      files,
      SUPPORTED_ATTACHMENT_MIME_TYPES
    )

    // Show error notification for unsupported files
    if (unsupportedFiles.length > 0) {
      alertToast({
        title: t("common.fileUpload.error.title"),
        description: t("common.fileUpload.error.unsupportedType", {
          files: unsupportedFiles.map((f) => f.name).join(", "),
          defaultValue: "Unsupported file types: {{files}}",
        }),
      })
    }

    // Process supported files if there are any
    if (supportedFiles.length > 0) {
      await onFilesSelected(supportedFiles)
    }
  }

  // Use the custom drag and drop hook
  const { isDragging, dragHandlers } = useDragAndDrop({
    onFilesDropped: processFiles,
    logPrefix: "FileUploadDialog",
  })

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    console.log(
      "FileUploadDialog - Selected files:",
      files.map((f) => f.name)
    )

    await processFiles(files)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("common.fileUpload.dialogTitle")}</DialogTitle>
          <DialogDescription>
            {t("common.fileUpload.description")}
          </DialogDescription>
        </DialogHeader>

        <div
          className={cn(
            "flex flex-col items-center justify-center p-10 border-2 border-dashed rounded-lg transition-colors",
            isDragging ? "border-primary bg-primary/10" : "border-gray-200"
          )}
          {...dragHandlers}
          data-testid="file-upload-dropzone"
        >
          <Upload className="h-10 w-10 text-gray-400 mb-4" />
          <p className="text-lg font-medium mb-2">
            {t("common.fileUpload.dragAndDrop")}
          </p>
          <p className="text-sm text-gray-500 mb-4">
            {t("common.fileUpload.dragAndDropDescription")}
          </p>
          <div className="relative">
            <Button type="button" variant="outline" className="relative">
              {t("common.fileUpload.selectFiles")}
              <input
                type="file"
                multiple
                onChange={handleFileSelect}
                className="absolute inset-0 w-full opacity-0 cursor-pointer"
                accept={FILE_ACCEPT_STRING}
                aria-label={t("common.fileUpload.selectFiles")}
              />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default FileUploadDialog
